import { ChangeDetectorRef, Component, OnDestroy, OnInit } from '@angular/core';
import { Router } from '@angular/router';
import { Browser } from '@capacitor/browser';
import { Capacitor } from '@capacitor/core';
import { Network } from '@capacitor/network';
import { MenuController, NavController } from '@ionic/angular';
import { Subscription } from 'rxjs';
import { OrganizationTokenModel } from 'src/app/api/proxy/organization/organization-models';
import { MemberProfileDimensionModel } from 'src/app/api/proxy/datapoints/models/member-profile-dimension-model';
import { ProfileModel } from 'src/app/api/proxy/vitalscore/vitalscore-models';
import { VivaDateInputComponent } from 'src/app/controls/viva-date-input/viva-date-input.component';
import { AuthService } from 'src/app/services/auth-service/auth.service';
import { UserData } from 'src/app/services/auth-service/user-data.model';
import { UserInfoService } from 'src/app/services/auth-service/user-info-service';
import { DashboardService } from 'src/app/services/dashboard-service/dashboard-service';
import { EnvironmentConfigService, ILocalAppEnvironment } from 'src/app/services/environment-config-service/environment-config.service';
import { EventService } from 'src/app/services/events/event.service';
import { MyProfileService } from 'src/app/services/my-profile-service/my-profile.service';
import { OrganizationService } from 'src/app/services/organization-service/organization.service';
import { PointsService } from 'src/app/services/points-service/points.service';
import { SubscriptionService } from 'src/app/services/subscription-service/subscription.service';
import { WhatsAppService } from 'src/app/services/whatsapp-service/whatsapp.service';
import { CurrentBalanceModel } from 'src/app/api/proxy/points/points-models';
import { isValidRewardsPartnerUrl, openRewardsPartnerUrl } from 'src/app/shared/utilities/rewards-partner-url.util';
import { TokenService } from 'src/app/services/token/token.service';
import { LocaleService } from 'src/app/services/locale-service/locale.service';
import { environment } from 'src/environments/environment';
import { sharedConfig } from 'src/environments/shared-config';

@Component({
  selector: 'app-main-menu',
  templateUrl: './main-menu.component.html',
  styleUrls: ['./main-menu.component.scss'],
  standalone: false
})
export class MainMenuComponent implements OnInit, OnDestroy {
  scanFriendAllowed: boolean = environment.scanFriendAllowed;
  isDashboardAllowed: boolean = false;
  chatEnabled: boolean = false;
  skinConditionEnabled: boolean = false;
  busyLoading: boolean = false;
  demoVisible: boolean = false;
  private subscriptions: Subscription[] = [];  // Store the subscription
  profile?: ProfileModel | null;
  emailAddress?: string;
  languageCode?: string;
  notificationCount?: number;
  emailVerified?: boolean | null;
  isProduction: boolean = true;
  installSource?: string | null;
  isLocalAccount: boolean = true;
  enableSubscriptions: boolean = false;
  enableExternalLogin: boolean = environment.enableExternalLogin;
  whatsappEnabled: boolean = environment.whatsappEnabled;
  /** When true, at least one profile dimension is configured for this user/org. */
  showProfileDimensionsMenu: boolean = false;
  pointsBalance?: CurrentBalanceModel | null = null;
  privacyPolicyUrl: string = environment.privacyPolicy;
  termsOfUseURL: string = environment.termsOfUse;
  faqUrl: string = environment.faqUrl;
  isOnline: boolean = true; // Default to online status

  constructor(
    private _navCtrl: NavController,
    private _DashboardService: DashboardService,
    private _authService: AuthService,
    private menu: MenuController,
    private _EnvironmentService: EnvironmentConfigService,
    private _SubscriptionService: SubscriptionService,
    private _MyProfileService: MyProfileService,
    private _OrganizationService: OrganizationService,
    private _PointsService: PointsService,
    private _WhatsAppService: WhatsAppService,
    private _changeDetectorRef: ChangeDetectorRef,
    private _tokenService: TokenService,
    private _router: Router,
    private _localeService: LocaleService,
  ) {

    this.subscriptions.push(
      EventService.environmentLoaded.subscribe(async (v: ILocalAppEnvironment) => {
        this.isProduction = v.production;
        this.installSource = v.installSource;
      }));

    this.subscriptions.push(
      EventService.organizationConfigLoaded.subscribe(async (v: OrganizationTokenModel) => {
        this.chatEnabled = v.aiChatEnabled == true;
        this.skinConditionEnabled = v.skinConditionEnabled == true;
        this.enableSubscriptions = v.subscriptionRequired == true && Capacitor.isNativePlatform();
        this._changeDetectorRef.markForCheck();
      }));

    this.subscriptions.push(
      EventService.myProfileUpdated.subscribe(async (profile: ProfileModel) => {
        this.profile = profile;
        const usr = await UserInfoService.getUserInfo();
        if (usr) {
          this.emailAddress = usr.email;
          this.languageCode = usr.language;
          this.emailVerified = usr.emailVerified;
          this.isLocalAccount = usr.localAccount;
        }

      }));

    this.subscriptions.push(
      EventService.userUpdated.subscribe(async (usr: UserData) => {
        if (usr) {
          this.emailAddress = usr.email;
          this.languageCode = usr.language;
          this.emailVerified = usr.emailVerified;
          this.isLocalAccount = usr.localAccount;
        }
      }));


    this.subscriptions.push(
      EventService.loadingEvent.subscribe(async (loading: number) => {
        this.busyLoading = loading > 0;
      }));

    this.subscriptions.push(
      EventService.userUpdated.subscribe(async (usr: UserData | null) => {
        this.isDashboardAllowed = await this._DashboardService.userIsAllowed();
      })
    );

    this.subscriptions.push(EventService.networkStatusChange.subscribe((isOnline: boolean) => {
      this.isOnline = isOnline;
    }));

    this.subscriptions.push(
      EventService.profileDimensionsUpdated.subscribe((dimensions: MemberProfileDimensionModel[]) => {
        this.applyProfileDimensions(dimensions);
      }),
    );

    this.subscriptions.push(
      EventService.userUpdated.subscribe((user: UserData | null) => {
        if (!user) {
          this.showProfileDimensionsMenu = false;
          this.pointsBalance = null;
          this._changeDetectorRef.markForCheck();
        }
      }),
    );

    this.subscriptions.push(
      EventService.onBiometricListUpdated.subscribe(() => {
        void this.refreshRewardsPartnerAvailabilityAfterHomeBootstrap();
      }),
    );


  }

  ngOnDestroy(): void {
    this.subscriptions.forEach((a) => { a.unsubscribe() });
  }

  async ngOnInit(): Promise<void> {
    this.isDashboardAllowed = await this._DashboardService.userIsAllowed();
    this.isProduction = this._EnvironmentService.IsProduction();
    this.installSource = this._EnvironmentService.InstallSource();
    this.profile = await this._MyProfileService.GetProfileStatic();

    const usr = await UserInfoService.getUserInfo();
    if (usr) {
      this.emailAddress = usr.email;
      this.languageCode = usr.language;
      this.emailVerified = usr.emailVerified;
      this.isLocalAccount = usr.localAccount;
      if (usr.email) {
        const eml = usr.email.toLowerCase().trim();
        const allAllowedEmails = environment.allowedDebugEmails_internal || []; //only internal debuggers.
        this.demoVisible = allAllowedEmails.some((email: string) => email.toLowerCase().trim() === eml);
        
       
      }
    }
    this.isOnline = true;
    const status = await Network.getStatus();
    if (!status.connected) {
      this.isOnline = false;
    }

    const cachedOrg = await this._OrganizationService.GetDefaultOrganization();
    if (cachedOrg) {
      this.skinConditionEnabled = cachedOrg.skinConditionEnabled === true;
      this._changeDetectorRef.markForCheck();
    }

    const accessToken = await this._tokenService.getTokenAsync();
    if (accessToken) {
      this.applyRewardsPartnerAvailability(await this._PointsService.currentBalanceStatic());
    }
  }

  get hasRewardsPartner(): boolean {
    return isValidRewardsPartnerUrl(this.pointsBalance?.rewardsPartnerUrl);
  }

  private async refreshRewardsPartnerAvailabilityAfterHomeBootstrap(): Promise<void> {
    const accessToken = await this._tokenService.getTokenAsync();
    if (!accessToken) {
      return;
    }

    const url = this._router.url ?? '';
    if (!url.startsWith('/home')) {
      return;
    }

    this.applyRewardsPartnerAvailability(await this._PointsService.currentBalanceStatic());
    this.applyRewardsPartnerAvailability(await this._PointsService.currentBalance(false));
  }

  private applyRewardsPartnerAvailability(balance: CurrentBalanceModel | null | undefined): void {
    this.pointsBalance = balance ?? null;
    this._changeDetectorRef.markForCheck();
  }

  private applyProfileDimensions(dimensions: MemberProfileDimensionModel[]): void {
    this.showProfileDimensionsMenu = dimensions.length > 0;
    this._changeDetectorRef.markForCheck();
  }

  get versionNumber(): string {
    return sharedConfig.version + ' (' + sharedConfig.build.toString() + ')';
  }

  get dateOfBirth(): string {
    if (!this.profile || !this.profile.dateOfBirth) {
      return '';
    }

    const date = VivaDateInputComponent.parseISODateString(this.profile.dateOfBirth);
    if (!date) return '';

    // Format the date according to the user's browser locale
    return new Intl.DateTimeFormat(this._localeService.getCulture(), {
      day: 'numeric',
      month: 'long',
      year: 'numeric'
    }).format(date);
  }

  async logout() {
    await this.menu.close();
    await this._authService.logout();
  }

  async myProfile() {
    await this.menu.close();
    await this._navCtrl.navigateRoot('home/myProfile');
  }

  async dimensions() {
    await this.menu.close();
    await this._navCtrl.navigateRoot('home/dimensions');
  }

  async mediaChatbot() {
    await this.menu.close();
    await this._navCtrl.navigateRoot('home/mediaChatbot');
  }

  async openUrl(theUrl: string) {
    await this.menu.close();
    setTimeout(async () => {
      await Browser.open({ url: theUrl });
    }, 10)
  }

  async gotoHelpCenter(): Promise<void> {
    const hcaOrgId = 'hca_gb' in environment ? (environment as { hca_gb: string }).hca_gb : undefined;
    if (hcaOrgId) {
      const organizationId = await this.resolveOrganizationId();
      if (organizationId === hcaOrgId) {
        await this.menu.close();
        await this._navCtrl.navigateForward('client/hca/startup-hca');
        return;
      }
    }

    await this.menu.close();
    if (environment.useHelpCenter) {
      await this._navCtrl.navigateForward('/v2/help-center');
      return;
    }
    await this.openUrl(this.faqUrl);
  }

  private async resolveOrganizationId(): Promise<string | undefined> {
    const defaultOrg = await this._OrganizationService.GetDefaultOrganization();
    if (defaultOrg?.id) {
      return defaultOrg.id;
    }
    const user = await UserInfoService.getUserInfo();
    const claims = user?.tokenClaims;
    return claims?.organizationId ?? claims?.extension_OrganizationId;
  }

  async deleteAccount() {
    await this.menu.close();
    await this._navCtrl.navigateRoot('deleteAccount');
  }

  async dashboard() {
    await this.menu.close();
    await this._navCtrl.navigateRoot('home/dashboard');
  }

  async gotoControlTest() {
    await this.menu.close();
    await this._navCtrl.navigateForward('control-demo');
  }

  async gotoMedia() {
    await this.menu.close();
    await this._navCtrl.navigateRoot('home/media');
  }

  async vivaScoreLite() {
    await this.menu.close();
    await this._navCtrl.navigateRoot('viva-lite/access-code');
  }

  async contactUs() {
    await this.menu.close();
    await this._navCtrl.navigateForward('home/contact-us');
  }

  async wearables() {
    await this.menu.close();
    await this._navCtrl.navigateForward('home/wearables');
  }

  async openRewards() {
    await this.menu.close();
    const url = this.pointsBalance?.rewardsPartnerUrl?.trim();
    if (!isValidRewardsPartnerUrl(url)) {
      return;
    }
    setTimeout(async () => {
      await openRewardsPartnerUrl(url!);
    }, 10);
  }

  async changePassword() {
    await this.menu.close();
    await this._navCtrl.navigateForward('home/change-password');
  }

  async changeEmail() {
    await this.menu.close();
    await this._navCtrl.navigateForward('home/change-email');
  }

  async linkExternalLogin() {
    await this.menu.close();
    await this._navCtrl.navigateForward('home/link-account');

  }

  async controlDemo() {
    await this.menu.close();
    await this._navCtrl.navigateForward('/control-demo');
  }

  async deviceHelpPage(){
      await this.menu.close();
    await this._navCtrl.navigateForward('home/devices-help');
  }


  async startOptInAndOpenWhatsApp(): Promise<void> {
    await this.menu.close();
    await this._WhatsAppService.startOptInAndOpenWhatsApp();
  }

  async visionWeb() {
    await this.menu.close();
    await this._navCtrl.navigateForward('/vision/vision-web-demo');
  }

  async skinConditionDemo() {
    await this.menu.close();
    await this._navCtrl.navigateForward('/skinCondition/detect');
  }

  async visionNative() {
    await this.menu.close();
    await this._navCtrl.navigateForward('/vision/vision-native-demo');
  }

  async manageSubscription() {
    await this.menu.close();
    await this._SubscriptionService.checkAndSubscribe((subsribed) => { }, true);
  }

  async telematics() {
    await this.menu.close();
    await this._navCtrl.navigateForward('home/telematics-detail');
  }

  async completedAssessments() {
    await this.menu.close();
    await this._navCtrl.navigateForward('home/questionnaire/report');
  }

  get telematicsAllowed(): boolean {
    if (!environment.allowTelematics) {
      return false;
    }

    const email = this.emailAddress;
    if (!email) {
      return false;
    }

    const normalizedEmail = email.toLowerCase().trim();
    const internalEmails = environment.allowedDebugEmails_internal || [];
    const externalEmails = environment.allowedDebugEmails_external || [];
    const allAllowedEmails = [...internalEmails, ...externalEmails];
    
    if (allAllowedEmails.length === 0) {
      return false;
    }

   
  return allAllowedEmails.some(
    allowedEmail => allowedEmail.toLowerCase() === normalizedEmail
  );
  }

  get skinDemoAllowed(): boolean {
    if (this.skinConditionEnabled) {
      return true;
    }

    const email = this.emailAddress;
    if (!email) {
      return false;
    }

    const normalizedEmail = email.toLowerCase().trim();
    const internalEmails = environment.allowedDebugEmails_internal || [];
    const externalEmails = environment.allowedDebugEmails_external || [];
    const allAllowedEmails = [...internalEmails, ...externalEmails];

    if (allAllowedEmails.length === 0) {
      return false;
    }

    return allAllowedEmails.some(
      allowedEmail => allowedEmail.toLowerCase() === normalizedEmail
    );
  }
}